import {
	SharepointApi,
	SharepointOwnerRow,
	SharepointSearchResponse,
} from "../../types/sharepointApi";
import {
	collabo,
	createNewFolder,
	deleteFiles,
	getFile,
	getFileMetadata,
	getFilePreview,
	getFileThumbnail,
	getRootFolderId,
	listFolderItems,
	moveFile,
	renameFile,
	searchSharepoint,
	uploadFiles,
} from "./welldocs";

export interface WelldocsApiOptions {
	tenantKey: string;
	/** Reference id of the well, operation or project that owns the folder. */
	keyId: string;
	/** Display name used when the root folder has to be created. */
	name: string;
	type: "well" | "operation" | "project";
	/** Full user record from the home context. */
	user: any;
	tenantContainerId?: string | null;
	operationCode?: string;
}

/** SharepointApi backed by the legacy `/sharepoint` module. */
export const createWelldocsApi = (
	options: WelldocsApiOptions
): SharepointApi => {
	const {
		tenantKey,
		keyId,
		name,
		type,
		user,
		tenantContainerId,
		operationCode,
	} = options;
	const owner = { email: user?.email, id: user?._id };
	const otherParams = { type, operation_code: operationCode };

	return {
		getRoot: async () => ({
			folderId:
				((await getRootFolderId(tenantKey, keyId, type)) as string) || null,
			containerId: tenantContainerId ?? null,
		}),
		listItems: async (folderId, containerId) => {
			const response: any = await listFolderItems(
				tenantKey,
				keyId,
				folderId,
				containerId,
				name,
				type
			);
			return {
				items: response?.items || [],
				folderId: response?.folderid ?? folderId,
				containerId: response?.containerid ?? containerId,
			};
		},
		getOwners: async () =>
			((await getFileMetadata(tenantKey, keyId)) as SharepointOwnerRow[]) || [],
		createFolder: (folderName, folderId, containerId, folderHierarchyIds) =>
			createNewFolder(
				folderName,
				tenantKey,
				keyId,
				folderId,
				containerId,
				folderHierarchyIds,
				owner,
				type,
				otherParams
			),
		uploadFiles: (files, folderId, containerId, folderHierarchyIds, socketId) =>
			uploadFiles(
				files,
				tenantKey,
				type,
				keyId,
				name,
				folderId,
				containerId,
				folderHierarchyIds,
				owner,
				socketId,
				otherParams
			),
		renameItem: (itemId, containerId, newName) =>
			renameFile(tenantKey, itemId, containerId, newName, owner),
		deleteItems: (itemIds, containerId, socketId) =>
			deleteFiles(tenantKey, itemIds, containerId, socketId, {
				type,
				operation_code: operationCode,
				user,
			}),
		moveItems: (itemIds, moveToId, containerId) =>
			moveFile(tenantKey, containerId, itemIds, moveToId, owner),
		getFile: (itemId, containerId) => getFile(tenantKey, itemId, containerId),
		getThumbnail: (itemId, containerId) =>
			getFileThumbnail(tenantKey, itemId, containerId),
		getPreview: (itemId, containerId) =>
			getFilePreview(tenantKey, itemId, containerId),
		getDownloadUrl: async (record) =>
			record?.["@microsoft.graph.downloadUrl"] || null,
		search: (query, limit) =>
			searchSharepoint(
				tenantKey,
				query,
				keyId,
				limit
			) as Promise<SharepointSearchResponse>,
		collaborate: (itemId, containerId) =>
			collabo(tenantKey, itemId, containerId, user),
		previewPath: (itemId, containerId, currentPath) =>
			`${currentPath}/preview?record=${encodeURIComponent(itemId)}&container=${encodeURIComponent(containerId)}`,
	};
};
